import { ChainId } from '@nominex/nomiswap-sdk'
import BigNumber from 'bignumber.js'
import bep20Abi from 'config/abi/IBep20Contract.json'
import { SerializedFarmConfig } from 'config/farms'
import stakingServiceAbi from 'config/abi/IStakingServiceContract.json'
import multicall from 'utils/multicall'
import { getStakingServiceContract } from 'utils/contractHelpers'
import { BIG_ZERO } from 'config/constants/bigNumber'

export const fetchFarmUserAllowances = async (
  account: string,
  chainId: ChainId,
  farmsToFetch: SerializedFarmConfig[],
) => {
  const calls = farmsToFetch.map((farm) => ({
    address: farm.lpAddress,
    name: 'allowance',
    params: [account, farm.stakingServiceAddress],
  }))

  const rawLpAllowances = await multicall(bep20Abi, calls, chainId)
  const parsedLpAllowances = rawLpAllowances.map((lpBalance) => new BigNumber(lpBalance).toJSON())

  return parsedLpAllowances
}

export const fetchFarmUserTokenBalances = async (
  account: string,
  chainId: ChainId,
  farmsToFetch: SerializedFarmConfig[],
) => {
  const calls = farmsToFetch.map((farm) => ({
    address: farm.lpAddress,
    name: 'balanceOf',
    params: [account],
  }))

  const rawTokenBalances = await multicall(bep20Abi, calls, chainId)
  const parsedTokenBalances = rawTokenBalances.map((tokenBalance) => new BigNumber(tokenBalance).toJSON())

  return parsedTokenBalances
}

export const fetchFarmUserStakedBalances = async (
  account: string,
  chainId: ChainId,
  farmsToFetch: SerializedFarmConfig[],
) => {
  const calls = farmsToFetch.map((farm) => ({
    address: farm.stakingServiceAddress,
    name: 'stakers',
    params: [account],
  }))

  const rawStakedBalances = await multicall(stakingServiceAbi, calls, chainId)
  const parsedStakedBalances = rawStakedBalances.map((stakedBalance) =>
    // eslint-disable-next-line no-underscore-dangle
    new BigNumber(stakedBalance.amount._hex).toJSON(),
  )

  return parsedStakedBalances
}

export const fetchFarmUserEarnings = async (
  account: string,
  chainId: ChainId,
  farmsToFetch: SerializedFarmConfig[],
) => {
  const calls = farmsToFetch.map((farm) => {
    const contract = getStakingServiceContract(farm.stakingServiceAddress, chainId)

    return contract.getReward({ from: account })
  })

  const rawEarningBalances = await Promise.allSettled(calls)
  const parsedEarningBalances = rawEarningBalances.map((result) =>
    // eslint-disable-next-line no-underscore-dangle
    result.status === 'fulfilled' ? new BigNumber(result.value._hex).toJSON() : BIG_ZERO.toJSON(),
  )

  return parsedEarningBalances
}
